import { DEFAULT_VIDEO_ASPECT_RATIO } from '@/lib/types/page-builder';
import { cn } from '@/lib/utils';

const EMBED_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';

interface EmbedFrameProps {
  /** Player src URL. Options are already baked into its query params. */
  src: string;
  /** Intrinsic width/height ratio of the embed. Falls back to 16:9. */
  aspectRatio?: number;
  /** Zoom of the video inside the frame, % of the frame (100–200). */
  scalePercent?: number;
  /** Pan of the zoomed video within the frame, % of the frame. */
  offsetX?: number;
  offsetY?: number;
  title?: string;
  className?: string;
  /** Overlays (e.g. editor controls) rendered above the iframe. */
  children?: React.ReactNode;
}

/**
 * The single way an embed is rendered — editor preview, builder preview
 * panel, and product page all use it. The stored {src, aspectRatio} pair is
 * the whole contract: the component never parses or rewrites anything, so a
 * stable src lets React reuse the DOM node and the video never reloads.
 */
export function EmbedFrame({
  src,
  aspectRatio,
  scalePercent,
  offsetX,
  offsetY,
  title = 'Video',
  className,
  children,
}: EmbedFrameProps) {
  // The frame is a fixed full-width aspect box; zoom/pan happen INSIDE it.
  // The oversized iframe is cropped by the frame's overflow-hidden, so the
  // layout of the page is never affected. The ±2px overscan hides sub-pixel
  // letterbox hairlines at the player's edges (2px, not 1px, so it survives
  // fractional device pixels at non-100% browser zoom). `colorScheme` must
  // match the embedded document: on a mismatch Chrome paints a cross-origin
  // iframe's background opaque WHITE, which turns those hairlines into
  // visible white lines in dark theme.
  const scale = scalePercent ?? 100;
  const iframeStyle: React.CSSProperties = {
    width: `calc(${scale}% + 4px)`,
    height: `calc(${scale}% + 4px)`,
    left: `calc(${(100 - scale) / 2 + (offsetX ?? 0)}% - 2px)`,
    top: `calc(${(100 - scale) / 2 + (offsetY ?? 0)}% - 2px)`,
    colorScheme: 'light',
  };

  return (
    <div
      className={cn('relative overflow-hidden bg-black w-full', className)}
      style={{ aspectRatio: aspectRatio || DEFAULT_VIDEO_ASPECT_RATIO }}
    >
      <iframe
        src={src}
        title={title}
        allow={EMBED_IFRAME_ALLOW}
        allowFullScreen
        className="absolute border-0"
        style={iframeStyle}
      />
      {children}
    </div>
  );
}
